import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Space,
  Popconfirm,
  Card,
  Upload,
  Spin,
  Select,
  Alert,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import { getStudentList, createStudent, updateStudent, deleteStudent, importStudents } from '../../services/student';
import { getAllCampuses } from '../../services/campus';
import { Student, PaginatedResponse, Campus } from '../../types';
import { antdMessage } from '../../utils/antdGlobal';
import { readCsvText, splitCsvLines, parseCsvLine } from '../../utils/csv';
import dayjs from 'dayjs';
import { useBreakpoint } from '../../hooks/useBreakpoint';

const StudentManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponse<Student> | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [searchKeyword, setSearchKeyword] = useState('');
  const { isMobile } = useBreakpoint();

  // 超级管理员相关状态
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [selectedCampusId, setSelectedCampusId] = useState<string>('');
  const [importErrors, setImportErrors] = useState<{ row: number; message: string }[]>([]);
  const [importResultVisible, setImportResultVisible] = useState(false);
  const userInfo = localStorage.getItem('userInfo');
  const userRole = userInfo ? JSON.parse(userInfo).role : null;

  // 获取校区列表（仅超级管理员需要）
  useEffect(() => {
    if (userRole === 'super_admin') {
      getAllCampuses().then(setCampuses).catch(() => {});
    }
  }, [userRole]);

  // 校区选择变化时重新加载数据
  useEffect(() => {
    if (userRole === 'super_admin') {
      fetchData({ page: 1 });
    }
  }, [selectedCampusId]);

  const fetchData = async (params?: any) => {
    setLoading(true);
    try {
      const res = await getStudentList({
        page: pagination.current,
        pageSize: pagination.pageSize,
        keyword: searchKeyword || undefined,
        // 超级管理员按选中校区过滤
        ...(userRole === 'super_admin' && selectedCampusId ? { campusId: selectedCampusId } : {}),
        ...params,
      });
      setData(res);
    } catch (error) {
      antdMessage.error('获取学生列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize, searchKeyword, selectedCampusId]);

  const handleAdd = () => {
    setEditingStudent(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Student) => {
    setEditingStudent(record);
    // 显示解密后的身份证号，让用户可以查看和修改
    const decryptedIdNumber = (record as any).idNumber || '';
    form.setFieldsValue({
      studentNo: record.studentNo,
      name: record.name,
      major: record.major,
      enrollmentDate: record.enrollmentDate ? dayjs(record.enrollmentDate) : null,
      idNumber: decryptedIdNumber,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteStudent(id);
      antdMessage.success('删除成功');
      fetchData();
    } catch (error) {
      antdMessage.error('删除失败');
    }
  };

  const handleSubmit = async (values: any) => {
    // 超级管理员添加学生时需要校区ID
    if (userRole === 'super_admin' && !editingStudent && !selectedCampusId) {
      antdMessage.warning('请先选择所属校区');
      return;
    }

    try {
      const submitData: any = {
        ...values,
        enrollmentDate: values.enrollmentDate?.format('YYYY-MM-DD'),
        // 超级管理员添加学生时带上校区ID
        ...(userRole === 'super_admin' && !editingStudent ? { campusId: selectedCampusId } : {}),
      };

      // 编辑模式下：如果身份证号未改变（与原值相同）则不传递
      if (editingStudent) {
        const originalIdNumber = (editingStudent as any).idNumber || '';
        if (!submitData.idNumber || submitData.idNumber === originalIdNumber) {
          delete submitData.idNumber; // 保持原有加密值不变
        }
      }

      if (editingStudent) {
        await updateStudent(editingStudent.id, submitData);
        antdMessage.success('更新成功');
      } else {
        await createStudent(submitData);
        antdMessage.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      antdMessage.error(editingStudent ? '更新失败' : '创建失败');
    }
  };

  /**
   * 批量导入学生
   * 编码自动检测（UTF-8/GBK），引号字段兼容，跳过行号纳入失败明细
   */
  const handleImport = async (file: File) => {
    // 超级管理员需要先选择校区
    if (userRole === 'super_admin' && !selectedCampusId) {
      antdMessage.warning('请先选择所属校区');
      return false;
    }

    try {
      const text = await readCsvText(file);
      const lines = splitCsvLines(text);
      const students = [];
      // 前端解析阶段被跳过的行（列数不足），与后端失败明细合并展示
      const skippedErrors: { row: number; message: string }[] = [];

      // 跳过表头
      for (let i = 1; i < lines.length; i++) {
        const cols = parseCsvLine(lines[i]);
        if (cols.length >= 4) {
          students.push({
            studentNo: cols[0].trim(),
            major: cols[1].trim(),
            name: cols[2].trim(),
            idNumber: cols[3].trim(),
            enrollmentDate: cols[4]?.trim() || undefined,
          });
        } else {
          skippedErrors.push({ row: i, message: `列数不足（仅 ${cols.length} 列），已跳过` });
        }
      }

      if (students.length === 0 && skippedErrors.length > 0) {
        setImportErrors(skippedErrors);
        setImportResultVisible(true);
        antdMessage.warning('CSV 文件无有效数据行，请检查文件格式');
        return false;
      }

      if (students.length > 0) {
        const res = await importStudents(students, userRole === 'super_admin' ? selectedCampusId : undefined);
        const allErrors = [...skippedErrors, ...(res.errors || [])];
        if (allErrors.length > 0) {
          antdMessage.warning(`导入完成，成功${res.success}条，失败${allErrors.length}条`);
          setImportErrors(allErrors);
          setImportResultVisible(true);
        } else {
          antdMessage.success(`导入完成，成功${res.success}条`);
        }
        fetchData();
      } else {
        antdMessage.warning('CSV 文件无有效数据行，请检查文件格式');
      }
    } catch (error) {
      antdMessage.error('导入失败');
    }
    return false;
  };

  const downloadTemplate = () => {
    // 使用通过格式验证的示例身份证号；带 BOM 保证 Excel 正确识别 UTF-8 中文
    const template = '学号,专业,姓名,身份证号,入学时间(可选)\n2024001,计算机科学,张三,110101200001011232,2024-09-01\n2024002,软件工程,李四,110101200001021238,2024-09-01';
    const blob = new Blob(['\ufeff' + template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = '学生导入模板.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: '学号',
      dataIndex: 'studentNo',
      key: 'studentNo',
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '专业',
      dataIndex: 'major',
      key: 'major',
      render: (val: string) => val || '-',
    },
    {
      title: '身份证号',
      dataIndex: 'maskedIdNumber',
      key: 'maskedIdNumber',
      render: (val: string) => val || '-',
    },
    {
      title: '入学时间',
      dataIndex: 'enrollmentDate',
      key: 'enrollmentDate',
      render: (date: string) => date || '-',
    },
    {
      title: '成绩数量',
      dataIndex: 'gradeCount',
      key: 'gradeCount',
      render: (count: number) => count || 0,
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Student) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除该学生吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="学生管理"
        extra={
          <Space wrap>
            <Input.Search
              placeholder="搜索学号/姓名/专业"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onSearch={() => { setPagination(p => ({ ...p, current: 1 })); }}
              allowClear
              style={{ width: 200 }}
            />
            {userRole === 'super_admin' && (
              <Select
                placeholder="选择校区"
                style={{ width: 160 }}
                value={selectedCampusId}
                onChange={setSelectedCampusId}
                allowClear
              >
                {campuses.map((c) => (
                  <Select.Option key={c.id} value={c.id}>
                    {c.campusName}
                  </Select.Option>
                ))}
              </Select>
            )}
            {!isMobile && (
              <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
                下载模板
              </Button>
            )}
            <Upload
              accept=".csv,.xlsx,.xls"
              beforeUpload={handleImport}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>{isMobile ? '导入' : '批量导入'}</Button>
            </Upload>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              {isMobile ? '添加' : '添加学生'}
            </Button>
          </Space>
        }
      >
        {isMobile ? (
          <Spin spinning={loading}>
            <div>
              {data?.list?.map((record: Student) => (
                <div key={record.id} style={{ marginBottom: 12 }}>
                  <Card size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{record.name}</div>
                        <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>学号：{record.studentNo}</div>
                        <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>专业：{record.major}</div>
                        <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>
                          入学：{record.enrollmentDate || '-'} · 成绩：{record.gradeCount || 0} 条
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                        <Popconfirm
                          title="确定删除该学生吗？"
                          onConfirm={() => handleDelete(record.id)}
                          okText="确定"
                          cancelText="取消"
                        >
                          <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                        </Popconfirm>
                      </div>
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </Spin>
        ) : (
          <Table
            columns={columns}
            dataSource={data?.list}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data?.total,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize: pageSize || 10 });
              },
            }}
          />
        )}
      </Card>

      {/* 导入错误明细 Modal */}
      <Modal
        title={`导入失败明细（共 ${importErrors.length} 条）`}
        open={importResultVisible}
        onCancel={() => setImportResultVisible(false)}
        footer={<Button type="primary" onClick={() => setImportResultVisible(false)}>知道了</Button>}
        width={500}
      >
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {importErrors.map((err, idx) => (
            <Alert
              key={idx}
              type="error"
              showIcon
              message={`第 ${err.row} 行：${err.message}`}
              style={{ marginBottom: 8 }}
            />
          ))}
        </div>
      </Modal>

      <Modal
        title={editingStudent ? '编辑学生' : '添加学生'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={isMobile ? '100%' : 500}
        style={isMobile ? { top: 0, maxWidth: '100vw' } : {}}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="studentNo"
            label="学号"
            rules={[{ required: true, message: '请输入学号' }]}
          >
            <Input disabled={!!editingStudent} placeholder="请输入学号" />
          </Form.Item>

          <Form.Item
            name="name"
            label="姓名"
            rules={[{ required: true, message: '请输入姓名' }]}
          >
            <Input placeholder="请输入姓名" />
          </Form.Item>

          <Form.Item
            name="major"
            label="专业"
            rules={[{ required: true, message: '请输入专业' }]}
          >
            <Input placeholder="请输入专业" />
          </Form.Item>

          <Form.Item
            name="idNumber"
            label="身份证号"
            rules={[
              { required: !editingStudent, message: '请输入身份证号' },
              { pattern: /^\d{17}[\dXx]$/, message: '身份证号格式不正确（18位）' },
            ]}
          >
            <Input
              placeholder={editingStudent ? '显示当前身份证号，如需修改请输入新的18位身份证号' : '请输入18位身份证号'}
            />
          </Form.Item>

          <Form.Item name="enrollmentDate" label="入学时间">
            <DatePicker style={{ width: '100%' }} placeholder="请选择入学时间" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default StudentManagement;
